import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import environmentService from '../services/environmentService';
import { getUserRoles } from '../services/authService';

const EnvironmentContext = createContext();

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

export const EnvironmentProvider = ({ children }) => {
  const { t } = useTranslation();
  const [environments, setEnvironments] = useState([]);
  const [currentEnvironment, setCurrentEnvironment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load environments on mount
  useEffect(() => {
    loadEnvironments();
  }, []);

  // Store selected environment in localStorage
  useEffect(() => {
    if (currentEnvironment) {
      localStorage.setItem('selectedEnvironment', JSON.stringify(currentEnvironment));
    }
  }, [currentEnvironment]);

  const loadEnvironments = async () => {
    try {
      setLoading(true);
      const envs = await environmentService.getEnvironments();
      setEnvironments(envs);

      // Try to restore previously selected environment from localStorage
      const savedEnvironment = localStorage.getItem('selectedEnvironment');
      if (savedEnvironment) {
        const parsedEnv = JSON.parse(savedEnvironment);
        // Check if the saved environment still exists
        const existingEnv = envs.find(env => env.environment_id === parsedEnv.environment_id);
        if (existingEnv) {
          setCurrentEnvironment(existingEnv);
        } else {
          // Fallback to default environment
          selectDefaultEnvironment(envs);
        }
      } else {
        // No saved environment, select default
        selectDefaultEnvironment(envs);
      }

      setError(null);
    } catch (err) {
      // Handle authentication errors gracefully
      if (err.response?.status === 401) {
        console.log('Authentication required for environments');
        // Don't set error for auth issues - let OrganizationRoute handle redirect  
        return;
      } else {
        console.error('Failed to load environments:', err);
        setError(t('errors.environments.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const selectDefaultEnvironment = (envs) => {
    // Find default environment or use first available
    const defaultEnv = envs.find(env => env.is_default) || envs[0];
    if (defaultEnv) {
      setCurrentEnvironment(defaultEnv);
    }
  };

  const switchEnvironment = async (environmentId) => {
    const env = environments.find(e => e.environment_id === environmentId);
    if (env) {
      setCurrentEnvironment(env);
      
      // You could add additional logic here, such as:
      // - Refreshing data for the new environment
      // - Updating URL parameters
      // - Clearing cached data from previous environment
      // - Triggering analytics events
      
      return env;
    }
    throw new Error('Environment not found');
  };

  const refreshEnvironments = () => {
    loadEnvironments();
  };

  const createEnvironment = async (environmentData) => {
    try {
      const newEnv = await environmentService.createEnvironment(environmentData);
      await loadEnvironments(); // Refresh the list
      return newEnv;
    } catch (err) {
      console.error('Failed to create environment:', err);
      throw err;
    }
  };

  const updateEnvironment = async (environmentId, environmentData) => {
    try {
      const updatedEnv = await environmentService.updateEnvironment(environmentId, environmentData);
      await loadEnvironments(); // Refresh the list
      
      // Update current environment if it was the one being updated
      if (currentEnvironment && currentEnvironment.environment_id === environmentId) {
        setCurrentEnvironment(updatedEnv);
      }
      
      return updatedEnv;
    } catch (err) {
      console.error('Failed to update environment:', err);
      throw err;
    }
  };

  const deleteEnvironment = async (environmentId) => {
    try {
      await environmentService.deleteEnvironment(environmentId);
      
      // If we deleted the current environment, switch to default
      if (currentEnvironment && currentEnvironment.environment_id === environmentId) {
        const remainingEnvs = environments.filter(env => env.environment_id !== environmentId);
        selectDefaultEnvironment(remainingEnvs);
      }
      
      await loadEnvironments(); // Refresh the list
    } catch (err) {
      console.error('Failed to delete environment:', err);
      throw err;
    }
  };

  // Environment type utilities
  const getEnvironmentTypeColor = (type) => {
    const colors = {
      development: '#4CAF50',
      staging: '#FF9800', 
      production: '#F44336',
      testing: '#2196F3'
    };
    return colors[type] || '#757575';
  };

  const formatEnvironmentType = (type) => {
    const types = {
      development: t('environments.mockData.development'),
      staging: t('environments.mockData.staging'),
      production: t('environments.mockData.production'),
      testing: t('environments.mockData.testing')
    };
    return types[type] || type;
  };

  // Check if user has permission for environment operations
  const canManageEnvironments = () => {
    // This could be enhanced to check actual user permissions
    // For now, we'll assume admin users can manage environments
    const roles = getUserRoles();
    const adminRoles = ['admin', 'Admin', 'superuser'];
    return roles.some(role => adminRoles.includes(role.name || role));
  };

  const value = {
    // State
    environments,
    currentEnvironment,
    loading,
    error,

    // Actions
    switchEnvironment,
    refreshEnvironments,
    createEnvironment,
    updateEnvironment,
    deleteEnvironment,

    // Utilities
    getEnvironmentTypeColor,
    formatEnvironmentType,
    canManageEnvironments,

    // Environment info
    getCurrentEnvironmentId: () => currentEnvironment?.environment_id,
    getCurrentEnvironmentName: () => currentEnvironment?.name || t('environments.noEnvironment'),
    getCurrentEnvironmentType: () => currentEnvironment?.environment_type,
    isCurrentEnvironmentDefault: () => currentEnvironment?.is_default || false,
    isCurrentEnvironmentActive: () => currentEnvironment?.status === 'active'
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export default EnvironmentContext;
